import type { Broker } from '@/services';
import { SearchFilterField } from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { f, useFilters } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
  Avatar,
  DataTable,
  DataTableColumn,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { Mail } from 'lucide-react';
import { useMemo } from 'react';
import { useAdvisoryBrokers } from '../hooks';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];

interface AdvisoryBrokersListProps {
  advisoryId: string;
}

export function AdvisoryBrokersList({ advisoryId }: AdvisoryBrokersListProps) {
  const { filters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(5),
  });

  const { data, isLoading, error } = useAdvisoryBrokers(advisoryId, {
    filter: filters.search || undefined,
    pageSize: filters.pageSize,
    pageNumber: filters.page,
  });

  const columns = useMemo<DataTableColumn<Broker>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => {
          const broker = row.original;
          return (
            <div className='flex items-center gap-3'>
              <Avatar.Root size='md' shape='circle'>
                <Avatar.Fallback />
              </Avatar.Root>
              <div className='flex flex-col gap-1'>
                <span className='font-gotham font-bold text-alba-black-500'>
                  {broker.name}
                </span>
                <div className='flex items-center gap-1'>
                  <Mail className='w-4 h-4 text-alba-gray-600' />
                  <span className='text-alba-gray-600 text-xs'>
                    {broker.email}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'licenseNumber',
        header: 'CNPJ',
        cell: ({ row }) => (
          <span className='text-alba-gray-700 text-sm'>
            {row.original.licenseNumber
              ? formatRegisterNumber(row.original.licenseNumber)
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'susepCode',
        header: 'SUSEP',
        cell: ({ row }) => (
          <span className='text-alba-gray-700 text-sm'>
            {row.original.susepCode || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const statusValue = String(row.original.status);
          const isActive =
            statusValue === 'active' || statusValue === '1';
          return (
            <Tag.Root
              variant='outline'
              colorScheme={isActive ? 'success' : 'gray'}
              size='lg'
            >
              <Tag.Label>{isActive ? 'Ativo' : 'Inativo'}</Tag.Label>
            </Tag.Root>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <div className='mb-6'>
        <h1 className='text-xl text-alba-gray-800 mt-2'>
          Grade de Corretores
        </h1>
        <p className='text-alba-gray-600 text-sm'>
          Corretores vinculados a esta assessoria
        </p>
      </div>

      <PageHeader.Filters className='mt-2'>
        <div className='mb-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome ou SUSEP...'
            className='w-full md:w-1/3'
          />
        </div>
      </PageHeader.Filters>

      <Page.Content>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar grade de corretores. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-3 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map((_, index) => (
                  <div
                    key={index}
                    className='h-28 animate-pulse rounded-2xl bg-alba-gray-200'
                  />
                ))
              ) : (
                (data?.data ?? []).map(broker => {
                  const statusValue = String(broker.status);
                  const isActive = statusValue === 'active' || statusValue === '1';

                  return (
                    <article
                      key={broker.id || broker.code || broker.email}
                      className='rounded-2xl bg-white p-3 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                    >
                      <div className='flex min-w-0 items-start gap-2'>
                        <Avatar.Root size='md' shape='circle'>
                          <Avatar.Fallback />
                        </Avatar.Root>
                        <div className='min-w-0 flex-1'>
                          <p className='font-gotham font-bold text-alba-black-500'>
                            {broker.name}
                          </p>
                          <p className='mt-1 truncate text-xs text-alba-gray-600'>
                            {broker.email}
                          </p>
                        </div>
                      </div>

                      <div className='mt-3 grid grid-cols-2 gap-2'>
                        <div>
                          <p className='text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                            SUSEP
                          </p>
                          <p className='text-sm text-alba-gray-700'>
                            {broker.licenseNumber || '—'}
                          </p>
                        </div>
                        <div>
                          <p className='text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                            Código
                          </p>
                          <p className='text-sm text-alba-gray-700'>
                            {broker.code || '—'}
                          </p>
                        </div>
                      </div>

                      <div className='mt-3'>
                        <Tag.Root
                          variant='outline'
                          colorScheme={isActive ? 'success' : 'gray'}
                          size='sm'
                        >
                          <Tag.Label>{isActive ? 'Ativo' : 'Inativo'}</Tag.Label>
                        </Tag.Root>
                      </div>
                    </article>
                  );
                })
              )}

              {!isLoading && (data?.data?.length ?? 0) === 0 && (
                <div className='rounded-2xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhum corretor encontrado.
                </div>
              )}

              {(data?.meta.totalPages ?? 0) > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={(data?.meta.totalPages ?? 0) * filters.pageSize}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    totalItems={data?.meta.total ?? 0}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        page: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, page: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden md:block'>
              <DataTable
                loading={isLoading}
                columns={columns}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalPages={data?.meta.totalPages ?? 0}
                data={data?.data ?? []}
                pageSize={filters.pageSize}
                totalItems={data?.meta.total ?? 0}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
    </>
  );
}
